import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Film, LogOut, LayoutDashboard, Search, BookMarked, Sparkles, Shield, ChevronDown, FolderCog, Bell, HardDrive } from 'lucide-react';
import { mediaUrl } from '../utils/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Navbar = () => {
  const { user, logout, borrowCount } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [manageOpen, setManageOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const manageRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (manageRef.current && !manageRef.current.contains(e.target)) setManageOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifs(data);
    } catch (e) { console.error('Failed to fetch notifications:', e); }
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const id = setInterval(fetchNotifications, 30000);
      return () => clearInterval(id);
    }
  }, [user]);

  const handleNotifClick = async (n) => {
    try {
      await axios.post(`${API}/notifications/${n.notification_id}/read`, {}, { withCredentials: true });
    } catch (e) { console.error('Failed to mark notification read:', e); }
    setNotifOpen(false);
    if (n.movie_id) navigate(`/movie/${n.movie_id}`);
    fetchNotifications();
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      fetchNotifications();
    } catch (e) { console.error('Failed to mark all read:', e); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'master_admin';
  const isMasterAdmin = user?.role === 'master_admin';
  const isMember = user?.role === 'member';

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navLinkClass = "text-sm font-medium px-3 py-2 rounded-lg transition-all duration-200 nav-hover-link";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glassmorphism" data-testid="main-navbar">
      <div className="px-2 sm:px-8 md:px-12 lg:px-16 py-3 sm:py-4 flex items-center justify-between gap-1 sm:gap-4">
        <div className="flex items-center gap-4 flex-shrink-0">
          <button onClick={() => navigate('/')} className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0" data-testid="navbar-logo">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1E3A8A' }}>
              <Film size={22} color="white" />
            </div>
            <span className="text-lg sm:text-xl font-bold tracking-tight" style={{ color: '#FFFFFF' }}>
              My<span style={{ color: '#3B82F6' }}>flix</span>
            </span>
          </button>

          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={() => navigate('/')}
              className={navLinkClass}
              style={{
                color: isActive('/', true) ? '#3B82F6' : '#FFFFFF',
                backgroundColor: isActive('/', true) ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                border: `1px solid ${isActive('/', true) ? '#3B82F6' : 'transparent'}`
              }}
              data-testid="navbar-home-link"
            >
              Home
            </button>

            {/* Surprise Me - always highlighted yellow (desktop only, mobile has it condensed) */}
            <button
              onClick={() => navigate('/surprise')}
              className={`${navLinkClass} flex items-center gap-1.5`}
              style={{
                color: '#F0C14B',
                backgroundColor: isActive('/surprise') ? 'rgba(240, 193, 75, 0.28)' : 'rgba(240, 193, 75, 0.12)',
                border: `1px solid ${isActive('/surprise') ? '#F0C14B' : 'rgba(240, 193, 75, 0.6)'}`
              }}
              data-testid="navbar-surprise-link"
            >
              <Sparkles size={14} /> Surprise Me
            </button>
          </div>

          {/* Search icon - ALWAYS visible (mobile + desktop) */}
          <button
            onClick={() => navigate('/search')}
            className="p-2 rounded-lg transition-all duration-200 nav-hover-link flex-shrink-0"
            style={{
              color: isActive('/search') ? '#3B82F6' : '#8197A4',
              backgroundColor: isActive('/search') ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
              border: `1px solid ${isActive('/search') ? '#3B82F6' : 'transparent'}`
            }}
            data-testid="navbar-search-button"
            aria-label="Search"
          >
            <Search size={20} />
          </button>

          {/* Surprise Me mobile-only compact icon (visible for ALL users including admins) */}
          <button
            onClick={() => navigate('/surprise')}
            className="inline-flex md:hidden p-2 rounded-lg transition-all duration-200 flex-shrink-0"
            style={{
              color: '#F0C14B',
              backgroundColor: isActive('/surprise') ? 'rgba(240, 193, 75, 0.28)' : 'rgba(240, 193, 75, 0.12)',
              border: `1px solid ${isActive('/surprise') ? '#F0C14B' : 'rgba(240, 193, 75, 0.6)'}`
            }}
            data-testid="navbar-surprise-mobile"
            aria-label="Surprise Me"
          >
            <Sparkles size={16} />
          </button>
        </div>

        <div className="flex items-center gap-0.5 sm:gap-3 flex-shrink-0">
          {/* Admin controls - visible on mobile + desktop */}
          {isAdmin && (
            <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
              {/* Manage dropdown (contains Manage Admin + Manage Movies) */}
              <div className="relative" ref={manageRef}>
                <button
                  onClick={() => setManageOpen(v => !v)}
                  className={`${navLinkClass} flex items-center gap-1.5`}
                  style={{
                    color: '#10B981',
                    backgroundColor: (isActive('/admin', true) || isActive('/manage-movies') || manageOpen)
                      ? 'rgba(16, 185, 129, 0.28)'
                      : 'rgba(16, 185, 129, 0.10)',
                    border: `1px solid ${(isActive('/admin', true) || isActive('/manage-movies') || manageOpen) ? '#10B981' : 'rgba(16, 185, 129, 0.5)'}`
                  }}
                  data-testid="navbar-manage-dropdown"
                >
                  <FolderCog size={14} /> <span className="hidden sm:inline">Manage</span> <ChevronDown size={12} className={`hidden sm:inline ${manageOpen ? 'rotate-180 transition-transform' : 'transition-transform'}`} />
                </button>
                {manageOpen && (
                  <div
                    className="absolute right-0 mt-2 w-56 rounded-lg overflow-hidden shadow-2xl z-50"
                    style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}
                    data-testid="manage-dropdown-menu"
                  >
                    <button
                      onClick={() => { setManageOpen(false); navigate('/admin'); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-opacity-70"
                      style={{
                        color: isActive('/admin', true) ? '#10B981' : '#FFFFFF',
                        backgroundColor: isActive('/admin', true) ? 'rgba(16, 185, 129, 0.15)' : 'transparent'
                      }}
                      data-testid="dropdown-manage-admin"
                    >
                      <LayoutDashboard size={16} />
                      <span className="text-sm font-medium">Manage Users</span>
                    </button>
                    <div style={{ borderTop: '1px solid #2A3F52' }} />
                    <button
                      onClick={() => { setManageOpen(false); navigate('/manage-movies'); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-opacity-70"
                      style={{
                        color: isActive('/manage-movies') ? '#10B981' : '#FFFFFF',
                        backgroundColor: isActive('/manage-movies') ? 'rgba(16, 185, 129, 0.15)' : 'transparent'
                      }}
                      data-testid="dropdown-manage-movies"
                    >
                      <Film size={16} />
                      <span className="text-sm font-medium">Manage Movies</span>
                    </button>
                    {isMasterAdmin && (
                      <>
                        <div style={{ borderTop: '1px solid #2A3F52' }} />
                        <button
                          onClick={() => { setManageOpen(false); navigate('/admin/storage'); }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-opacity-70"
                          style={{
                            color: isActive('/admin/storage') ? '#3B82F6' : '#FFFFFF',
                            backgroundColor: isActive('/admin/storage') ? 'rgba(59, 130, 246, 0.15)' : 'transparent'
                          }}
                          data-testid="dropdown-storage-manager"
                        >
                          <HardDrive size={16} />
                          <span className="text-sm font-medium">Storage Manager</span>
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Master - red */}
              {isMasterAdmin && (
                <button
                  onClick={() => navigate('/master-admin')}
                  className={`${navLinkClass} flex items-center gap-1.5`}
                  style={{
                    color: '#EF4444',
                    backgroundColor: isActive('/master-admin') ? 'rgba(239, 68, 68, 0.28)' : 'rgba(239, 68, 68, 0.10)',
                    border: `1px solid ${isActive('/master-admin') ? '#EF4444' : 'rgba(239, 68, 68, 0.5)'}`
                  }}
                  data-testid="navbar-master-admin-link"
                >
                  <Shield size={14} /> <span className="hidden sm:inline">Master</span>
                </button>
              )}
            </div>
          )}

          {/* Notifications bell */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => setNotifOpen(v => !v)}
              className="relative p-1.5 sm:p-2.5 rounded-lg transition-all duration-200 nav-hover-link"
              style={{
                color: notifs.unread > 0 ? '#F0C14B' : '#8197A4',
                backgroundColor: notifOpen ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                border: `1px solid ${notifOpen ? '#3B82F6' : 'transparent'}`
              }}
              data-testid="notifications-button"
              aria-label="Notifications"
            >
              <Bell size={20} />
              {notifs.unread > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#EF4444', color: 'white', border: '2px solid #0F171E' }}
                  data-testid="notifications-badge"
                >
                  {notifs.unread > 9 ? '9+' : notifs.unread}
                </span>
              )}
            </button>
            {notifOpen && (
              <div
                className="absolute right-0 mt-2 w-80 rounded-lg overflow-hidden shadow-2xl z-50"
                style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}
                data-testid="notifications-dropdown"
              >
                <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid #2A3F52' }}>
                  <p className="font-semibold" style={{ color: '#FFFFFF' }}>Notifications</p>
                  {notifs.unread > 0 && (
                    <button onClick={markAllRead}
                      className="text-xs font-semibold"
                      style={{ color: '#3B82F6' }}
                      data-testid="mark-all-read-btn">
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifs.items.length === 0 ? (
                    <div className="p-6 text-center">
                      <Bell size={24} className="mx-auto mb-2" style={{ color: '#8197A4' }} />
                      <p className="text-sm" style={{ color: '#8197A4' }}>No notifications yet</p>
                    </div>
                  ) : (
                    notifs.items.map(n => (
                      <button
                        key={n.notification_id}
                        onClick={() => handleNotifClick(n)}
                        className="w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5"
                        style={{
                          backgroundColor: n.read ? 'transparent' : 'rgba(59, 130, 246, 0.08)',
                          borderBottom: '1px solid rgba(42, 63, 82, 0.5)'
                        }}
                        data-testid={`notification-${n.notification_id}`}
                      >
                        {n.movie?.poster_path ? (
                          <img src={mediaUrl(n.movie.poster_path, API)} alt="" className="w-10 h-14 rounded object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-14 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0F171E' }}>
                            <Film size={16} style={{ color: '#8197A4' }} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm" style={{ color: '#FFFFFF' }}>{n.message}</p>
                          <p className="text-[10px] mt-1" style={{ color: '#8197A4' }}>
                            {new Date(n.created_at).toLocaleString()}
                          </p>
                        </div>
                        {!n.read && (
                          <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#3B82F6' }} />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* My Borrows - icon-only on mobile, full label on sm+ */}
          <button
            onClick={() => navigate('/my-borrows')}
            className="flex items-center gap-2 sm:gap-3 p-1 sm:px-3 sm:py-2 rounded-lg transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: isActive('/my-borrows') ? 'rgba(59, 130, 246, 0.2)' : '#1A2632',
              border: `1px solid ${isActive('/my-borrows') ? '#3B82F6' : '#2A3F52'}`
            }}
            data-testid="navbar-borrows-link"
          >
            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#1E3A8A' }}>
              <BookMarked size={16} color="white" />
            </div>
            <div className="hidden sm:block text-left leading-tight">
              <p className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>My Borrows</p>
              <p className="text-[10px] font-mono" style={{ color: '#8197A4' }} data-testid="borrow-counter-text">
                {borrowCount.active_count}/{borrowCount.max_limit}
              </p>
            </div>
          </button>

          {/* Square user badge - icon-only on mobile */}
          <button
            onClick={() => isMember ? navigate('/profile') : null}
            disabled={!isMember}
            className="flex items-center gap-2 sm:gap-3 p-1 sm:px-3 sm:py-2 rounded-lg transition-all duration-200 hover:scale-105 disabled:cursor-default"
            style={{
              backgroundColor: isActive('/profile') ? 'rgba(59, 130, 246, 0.2)' : '#1A2632',
              border: `1px solid ${isActive('/profile') ? '#3B82F6' : '#2A3F52'}`
            }}
            data-testid="navbar-user-badge"
            title={isMember ? 'My Account' : user?.name}
          >
            <div
              className="w-7 h-7 sm:w-9 sm:h-9 rounded-md flex items-center justify-center text-sm font-bold overflow-hidden flex-shrink-0"
              style={{ backgroundColor: '#1E3A8A', color: 'white' }}
            >
              {user?.avatar_path ? (
                <img src={mediaUrl(user.avatar_path, API)} alt={user?.name} className="w-full h-full object-cover" />
              ) : (
                user?.name?.[0]?.toUpperCase() || 'U'
              )}
            </div>
            <div className="hidden sm:block text-left leading-tight">
              <p className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>{user?.name}</p>
              <p className="text-[10px] font-mono" style={{ color: '#8197A4' }}>{user?.membership_id}</p>
            </div>
          </button>

          {/* Logout - icon only on mobile, with label on sm+ */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-lg transition-all duration-200 nav-hover-link"
            style={{ color: '#8197A4', border: '1px solid transparent' }}
            data-testid="navbar-logout-button"
          >
            <LogOut size={18} />
            <span className="hidden sm:inline text-sm font-medium">Logout</span>
          </button>
        </div>
      </div>
    </nav>
  );
};
